import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { offerApi } from '../../lib/offerApi';
import { toast } from 'sonner';
import {
  FileText, Plus, Search, Filter, Send, Clock, CheckCircle2,
  XCircle, Eye, ChevronRight, TrendingUp, AlertCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-50 text-blue-600', icon: Send },
  selecting: { label: 'Selecting Benefits', color: 'bg-amber-50 text-amber-600', icon: Clock },
  awaiting_acceptance: { label: 'Awaiting Acceptance', color: 'bg-purple-50 text-purple-600', icon: AlertCircle },
  accepted: { label: 'Accepted', color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle2 },
  declined: { label: 'Declined', color: 'bg-red-50 text-red-600', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-stone-100 text-stone-400', icon: Clock },
};

const TABS = [
  { key: null, label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'selecting', label: 'Selecting' },
  { key: 'awaiting_acceptance', label: 'Awaiting' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'expired', label: 'Expired' },
];

export default function OfferLettersPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadOffers();
  }, [activeTab]);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const data = await offerApi.list(activeTab);
      setOffers(data.offers || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error('Failed to load offer letters');
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = useMemo(() => {
    if (!searchQuery) return offers;
    const q = searchQuery.toLowerCase();
    return offers.filter(o =>
      o.candidate_name?.toLowerCase().includes(q) ||
      o.position_title?.toLowerCase().includes(q) ||
      o.candidate_email?.toLowerCase().includes(q)
    );
  }, [offers, searchQuery]);

  const totalSent = (stats.sent || 0) + (stats.selecting || 0) + (stats.awaiting_acceptance || 0) + (stats.accepted || 0) + (stats.declined || 0) + (stats.expired || 0);
  const accepted = stats.accepted || 0;
  const acceptRate = totalSent > 0 ? Math.round(accepted / totalSent * 100) : 0;

  const fmt = (n) => n ? `₦${Number(n).toLocaleString()}` : '₦0';

  return (
    <div className="space-y-6" data-testid="offer-letters-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2D3436] tracking-tight font-[Manrope]">Offer Letters</h1>
          <p className="text-sm text-[#636E72] mt-1">Create and manage offer letters with benefits selection</p>
        </div>
        <button
          onClick={() => navigate('/company-admin/offer-letters/create')}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl font-semibold text-sm shadow-[0_2px_10px_rgba(214,77,122,0.3)] transition-all active:scale-95"
          data-testid="create-offer-btn"
        >
          <Plus className="w-4 h-4" /> Create New Offer
        </button>
      </div>

      {/* Funnel Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm">
          <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider">Total Sent</p>
          <p className="text-2xl font-extrabold text-[#2D3436] mt-1 font-[Manrope]">{totalSent}</p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm">
          <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider">Selecting</p>
          <p className="text-2xl font-extrabold text-amber-500 mt-1 font-[Manrope]">{(stats.selecting || 0) + (stats.awaiting_acceptance || 0)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm">
          <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider">Accepted</p>
          <p className="text-2xl font-extrabold text-emerald-500 mt-1 font-[Manrope]">{accepted}</p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider">Acceptance Rate</p>
            <TrendingUp className="w-3 h-3 text-emerald-500" />
          </div>
          <p className="text-2xl font-extrabold text-[#2D3436] mt-1 font-[Manrope]">{acceptRate}%</p>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm">
        <div className="flex items-center justify-between border-b border-stone-100 px-5 pt-4">
          <div className="flex gap-1 overflow-x-auto pb-0">
            {TABS.map(tab => (
              <button
                key={tab.key || 'all'}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'text-[#D64D7A] border-b-2 border-[#D64D7A] bg-pink-50/50'
                    : 'text-[#636E72] hover:text-[#2D3436]'
                }`}
                data-testid={`tab-${tab.key || 'all'}`}
              >
                {tab.label}
                {tab.key && stats[tab.key] ? <span className="ml-1.5 text-xs bg-stone-100 px-1.5 py-0.5 rounded-full">{stats[tab.key]}</span> : null}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-stone-50 rounded-lg px-3 py-2 text-sm min-w-[200px] ml-4 mb-1">
            <Search className="w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent outline-none text-sm w-full"
              data-testid="search-offers"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-3 border-pink-300 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-[#636E72] font-medium">No offer letters found</p>
              <p className="text-sm text-stone-400 mt-1">Create your first offer letter to get started</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-[#636E72] uppercase tracking-wider border-b border-stone-100">
                  <th className="px-5 py-3">Candidate</th>
                  <th className="px-5 py-3">Position</th>
                  <th className="px-5 py-3">Package</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Sent</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.map(offer => {
                  const sc = STATUS_CONFIG[offer.status] || STATUS_CONFIG.draft;
                  const Icon = sc.icon;
                  const totalPkg = offer.gross_monthly + offer.benefits_budget;
                  return (
                    <tr key={offer.id} className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors" data-testid={`offer-row-${offer.id}`}>
                      <td className="px-5 py-4">
                        <p className="font-medium text-[#2D3436]">{offer.candidate_name}</p>
                        <p className="text-xs text-[#636E72]">{offer.candidate_email}</p>
                      </td>
                      <td className="px-5 py-4 text-[#2D3436]">{offer.position_title}</td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-[#2D3436]">{fmt(totalPkg)}/mo</p>
                        <p className="text-xs text-[#636E72]">Salary: {fmt(offer.gross_monthly)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                          <Icon className="w-3 h-3" /> {sc.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[#636E72]">
                        {offer.created_at ? new Date(offer.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' }) : '-'}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => navigate(`/company-admin/offer-letters/${offer.id}`)}
                          className="text-[#D64D7A] hover:text-[#B52D5B] font-medium text-xs flex items-center gap-1 ml-auto"
                          data-testid={`view-offer-${offer.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> View <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
